import {
  Box,
  FormRadio,
  FormSelect,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@husqvarna/ui-core";
import { useEffect, useRef, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import debounce from "lodash/debounce";
import PageContainer from "../../shared/components/PageContainer/PageContainer";
import { RootState } from "../../shared/store";
import UpdateGeneralInfoModel from "../models/UpdateGeneralInfoModel";
import Project from "../models/Project";
import { useForm } from "react-hook-form";
import { useUpdateGeneralInfoMutation } from "../projectApi";
import FormTextWithSuffix from "../../shared/components/FormTextWithSuffix";

const createDefaultValues = (project?: Project) => ({
  projectId: project?.id ?? "",
  monthsToCutPerYear: project?.seasonDurationMonths ?? 1,
  usingRenewableEnergy: project?.renewableEnergy ?? false,
  hourlyLabourRate: project?.hourlyLabourRate,
  electricityCostPerKWh: project?.electricityPricePerKwh,
  labourHoursPerWeek: project?.proposal.roboticsLabourHoursPerWeek,
});

const GeneralInfo = () => {
  const intl = useIntl();
  const project = useSelector(
    (state: RootState) => state.project.selectedProject,
  );
  const {
    register,
    setValue,
    reset,
    watch,
    trigger,
    formState: { errors, isDirty },
  } = useForm<UpdateGeneralInfoModel>({
    defaultValues: createDefaultValues(project),
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const [updateGeneralInfo] = useUpdateGeneralInfoMutation();
  const isSavingRef = useRef(false);

  const saveFunction = useCallback(
    (model: UpdateGeneralInfoModel) => {
      if (isSavingRef.current || !project) return;
      isSavingRef.current = true;

      updateGeneralInfo(model)
        .unwrap()
        .then(() => {
          // Update the form with the latest values
          Object.entries(model).forEach(([key, value]) => {
            setValue(key as keyof UpdateGeneralInfoModel, value, {
              shouldDirty: false,
            });
          });
          trigger();
        })
        .catch((err) => {
          console.error("Save failed:", err);
        })
        .finally(() => {
          isSavingRef.current = false;
        });
    },
    [updateGeneralInfo, project, trigger, setValue],
  );

  const debouncedSave = useMemo(
    () => debounce(saveFunction, 750),
    [saveFunction],
  );

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (type === "change" && !isSavingRef.current) {
        debouncedSave(value as UpdateGeneralInfoModel);
      }
    });
    return () => {
      subscription.unsubscribe();
      debouncedSave.cancel();
    };
  }, [watch, debouncedSave]);

  useEffect(() => {
    if (!project || isDirty) return;
    reset(createDefaultValues(project));
  }, [project, reset]);

  const seasonDurationOptions = Array.from({ length: 12 }, (_, i) => ({
    label: `${i + 1}`,
    value: i + 1,
  }));

  return (
    <>
      <PageContainer
        headline={intl.formatMessage({
          id: "projects.generalInfo.heading",
          defaultMessage: "General information",
        })}
        description={intl.formatMessage({
          id: "projects.generalInfo.subtitle",
          defaultMessage:
            "Fill out the information to estimate cost for maintaining robotic mowers. ",
        })}
      >
        <Box container flexDirection="column">
          {project && (
            <form autoComplete="off">
              <Grid container spacing={6}>
                <Grid item sm={12} md={6} lg={4}>
                  <Box container height={6} mb={2}>
                    <Typography
                      tag="label"
                      variant="Body2"
                      className="tcsp-medium"
                    >
                      <FormattedMessage
                        id="projects.generalInfo.seasonDurationInfo"
                        defaultMessage="Number of months of yearly mowing"
                      />
                    </Typography>
                  </Box>
                  <FormSelect
                    options={seasonDurationOptions}
                    selectedValue={watch("monthsToCutPerYear")}
                    label=""
                    {...register("monthsToCutPerYear", {
                      onChange: (e) => {
                        setValue("monthsToCutPerYear", e.target.value, {
                          shouldDirty: true,
                        });
                      },
                    })}
                  />
                </Grid>
                <Grid item sm={12} md={6} lg={4}>
                  <Box container height={6} alignItems="center" mb={2}>
                    <Typography
                      tag="label"
                      variant="Body2"
                      className="tcsp-medium"
                      htmlFor="hourlyLabour"
                    >
                      <FormattedMessage
                        id="projects.generalInfo.hourlyLabourHeading"
                        defaultMessage="Hourly labour rate"
                      />
                    </Typography>
                    <Tooltip
                      toolTipText={intl.formatMessage({
                        id: "project.generalInfo.hourlyLabourTooltip",
                        defaultMessage: "For both current setup and new setup",
                      })}
                    >
                      <IconButton mx={2} icon="info" size="Medium" />
                    </Tooltip>
                  </Box>

                  <FormTextWithSuffix
                    id="hourlyLabour"
                    placeholder={intl.formatMessage({
                      id: "projects.generalInfo.hourlyLabourLabel",
                      defaultMessage: "Rate",
                    })}
                    value={watch("hourlyLabourRate")}
                    {...register("hourlyLabourRate", {
                      pattern: {
                        value: /^\d+\.?\d*$/,
                        message: intl.formatMessage({
                          id: "general.formValidation.notDecimal",
                          defaultMessage:
                            "Needs to be a number or decimal (with period as decimal sign).",
                          description: "Form error message for not a decimal",
                        }),
                      },
                    })}
                    error={errors.hourlyLabourRate?.message}
                    suffix={project?.region.currency}
                  />
                </Grid>
                <Grid item sm={12} md={6} lg={4}>
                  <Box height={6} mb={2}>
                    <Typography
                      tag="label"
                      variant="Body2"
                      className="tcsp-medium"
                      htmlFor="maintenance"
                    >
                      <FormattedMessage
                        id="projects.generalInfo.labourhoursperweek"
                        defaultMessage="Maintenance hours per week"
                      />
                    </Typography>
                  </Box>
                  <FormTextWithSuffix
                    id="maintenance"
                    suffix={intl.formatMessage({id: 'general.hourAbbreviation', defaultMessage: 'h'})}
                    placeholder={intl.formatMessage({
                      id: "general.hours",
                      defaultMessage: "Hours",
                    })}
                    value={watch("labourHoursPerWeek")}
                    {...register("labourHoursPerWeek", {
                      pattern: {
                        value: /^\d+\.?\d*$/,
                        message: intl.formatMessage({
                          id: "general.formValidation.notDecimal",
                          defaultMessage:
                            "Needs to be a number or decimal (with period as decimal sign).",
                          description: "Form error message for not a decimal",
                        }),
                      },
                    })}
                    error={errors.labourHoursPerWeek?.message}
                  />
                </Grid>
              </Grid>
              <Box mt={12}>
                <Typography
                  tag="p"
                  variant="Body2"
                  className="tcsp-medium"
                  mb={4}
                >
                  <FormattedMessage
                    id="projects.generalInfo.renewableInfo"
                    defaultMessage="Does the customer use renewable electricity?"
                  />
                </Typography>
                <Box flexDirection={"row"} alignItems="center">
                  <FormRadio
                    mr={9}
                    mt={2}
                    label={intl.formatMessage({
                      id: "general.yes",
                      defaultMessage: "Yes",
                    })}
                    checked={watch("usingRenewableEnergy") === true}
                    onChange={() => {
                      setValue("usingRenewableEnergy", true);
                      debouncedSave({ ...watch(), usingRenewableEnergy: true });
                    }}
                  />
                  <FormRadio
                    label={intl.formatMessage({
                      id: "general.no",
                      defaultMessage: "No",
                    })}
                    checked={watch("usingRenewableEnergy") === false}
                    onChange={() => {
                      setValue("usingRenewableEnergy", false);
                      debouncedSave({
                        ...watch(),
                        usingRenewableEnergy: false,
                      });
                    }}
                  />
                </Box>
                <Box container alignItems="center" mt={12} mb={2}>
                  <Typography
                    tag="label"
                    variant="Body2"
                    className="tcsp-medium"
                    htmlFor="electricityCost"
                  >
                    <FormattedMessage
                      id="projects.generalInfo.electicityCostInfo"
                      defaultMessage="Electricity cost per kWh"
                    />
                  </Typography>
                </Box>
                <Grid container spacing={8}>
                  <Grid item sm={12} md={6}>
                    <FormTextWithSuffix
                      id="electricityCost"
                      placeholder={intl.formatMessage({
                        id: "projects.generalInfo.electicityCostLabel",
                        defaultMessage: "Amount",
                      })}
                      value={watch("electricityCostPerKWh")}
                      suffix={project?.region.currency}
                      {...register("electricityCostPerKWh", {
                        pattern: {
                          value: /^\d+\.?\d*$/,
                          message: intl.formatMessage({
                            id: "general.formValidation.notDecimal",
                            defaultMessage:
                              "Needs to be a number or decimal (with period as decimal sign).",
                            description: "Form error message for not a decimal",
                          }),
                        },
                      })}
                      error={errors.electricityCostPerKWh?.message}
                    />
                  </Grid>
                </Grid>
              </Box>
            </form>
          )}
        </Box>
      </PageContainer>
    </>
  );
};
export default GeneralInfo;
